import React, { useEffect, useState } from "react";
import {
  getSalaryEmployeeDetailV2,
  getSalaryEmployeeHistoryV2,
  patchSalaryEmployeeHistoryV2,
  patchSubstitutionPaymentPercentV2,
} from "../../../../api/timesheetApi";

const FIELD_TO_KIND = {
  annualLeave: "vacation",
  sickLeave: "sick_leave",
  substitution: "substitution",
};

const KIND_META = {
  vacation: {
    title: "Расчёт отпускных",
    formulaLines: ["СрД = ΣЗП12 / ΣД12", "Отпускные = СрД × Д_отп"],
    daysLabel: "Дни отпуска",
  },
  sick_leave: {
    title: "Расчёт больничных",
    formulaLines: ["СрД = ΣЗП12 / ΣД12", "Больничные = СрД × Д_боль"],
    daysLabel: "Дни больничного",
  },
  substitution: {
    title: "Расчёт замещения",
    formulaLines: [
      "Замещение = (ЗП отсутствующего / Р.дн.) × Дн. замещения × % замещения",
    ],
    daysLabel: null,
  },
};

const formatCurrency = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(
    Number(n) || 0,
  );

const keepDigitsAndDot = (value) =>
  String(value ?? "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");

const formatPeriodRange = (periodStr) => {
  if (!periodStr || typeof periodStr !== "string") return "—";
  const trimmed = periodStr.trim();
  if (!trimmed) return "—";
  return trimmed.replace(/\s*-\s*/g, "–");
};

const MRP_BY_YEAR = {
  2025: 3932,
  2026: 4325,
};

const RU_MONTHS_TO_NUMBER = {
  январь: 1,
  января: 1,
  февраль: 2,
  февраля: 2,
  март: 3,
  марта: 3,
  апрель: 4,
  апреля: 4,
  май: 5,
  мая: 5,
  июнь: 6,
  июня: 6,
  июль: 7,
  июля: 7,
  август: 8,
  августа: 8,
  сентябрь: 9,
  сентября: 9,
  октябрь: 10,
  октября: 10,
  ноябрь: 11,
  ноября: 11,
  декабрь: 12,
  декабря: 12,
};

/** Первое из значений, которое можно интерпретировать как число (включая 0). */
const coalesceNumeric = (...rawValues) => {
  for (const raw of rawValues) {
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const pickAbsentUserFullName = (item) => {
  const raw =
    item?.absent_user_full_name ??
    item?.absentUserFullName ??
    item?.absent_user?.full_name ??
    null;
  if (typeof raw !== "string") return "";
  return raw.trim();
};

const pickAbsentUserPosition = (item) => {
  const raw =
    item?.absent_user_position ??
    item?.absentUserPosition ??
    item?.absent_user?.position ??
    null;
  if (typeof raw !== "string") return "";
  return raw.trim();
};

const normalizeSubstitutions = (detail) => {
  if (!detail || typeof detail !== "object") return [];

  if (Array.isArray(detail.substitutions) && detail.substitutions.length > 0) {
    return detail.substitutions.map((item) => ({
      period: typeof item?.period === "string" ? item.period : "",
      days: coalesceNumeric(item?.days, 0) || 0,
      paymentPercent:
        coalesceNumeric(item?.payment_percent, item?.paymentPercent, 0) || 0,
      absentUserId: coalesceNumeric(
        item?.absent_user_id,
        item?.absentUserId,
      ),
      absentUserFullName: pickAbsentUserFullName(item),
      absentUserPosition: pickAbsentUserPosition(item),
      absentUserSalary:
        coalesceNumeric(item?.absent_user_salary, item?.absentSalary, 0) || 0,
      amount: coalesceNumeric(item?.amount, 0) || 0,
      // payment_id из substitutions[] (salary-v2/employee detail).
      paymentId: coalesceNumeric(
        item?.payment_id,
        item?.substitution_payment_id,
      ),
    }));
  }

  const days = coalesceNumeric(detail.substitution_days, 0) || 0;
  const amount = coalesceNumeric(detail.substitution_amount, 0) || 0;
  const period =
    typeof detail.substitution_period === "string"
      ? detail.substitution_period
      : "";
  const paymentId = coalesceNumeric(detail.substitution_payment_id);
  if (!period && days === 0 && amount === 0 && paymentId === null) return [];

  return [
    {
      period,
      days,
      paymentPercent:
        coalesceNumeric(detail.substitution_payment_percent, 0) || 0,
      absentUserId: coalesceNumeric(
        detail.absent_user_id,
        detail.absentUserId,
      ),
      absentUserFullName: pickAbsentUserFullName(detail),
      absentUserPosition: pickAbsentUserPosition(detail),
      absentUserSalary:
        coalesceNumeric(
          detail.absent_user_salary,
          detail.substitution_absent_user_salary,
          detail.absentSalary,
        ) || 0,
      amount,
      paymentId,
    },
  ];
};

const getSubstitutionItemKey = (item, index) => {
  if (item?.paymentId != null) return String(item.paymentId);
  if (item?.absentUserId != null && item?.period) {
    return `${item.absentUserId}-${item.period}`;
  }
  if (item?.absentUserId != null) return `absent-${item.absentUserId}-${index}`;
  return `idx-${index}`;
};

const formatAbsentUserLabel = (item) => {
  if (!item) return "";
  const name = item.absentUserFullName || "";
  const position = item.absentUserPosition || "";
  if (name && position) return `${name} · ${position}`;
  return name || position;
};

const buildSubstitutionPercentInputs = (items, detail) => {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      "idx-0": String(
        Math.max(
          0,
          Number(coalesceNumeric(detail?.substitution_payment_percent, 0)) || 0,
        ),
      ),
    };
  }

  return items.reduce((acc, item, index) => {
    acc[getSubstitutionItemKey(item, index)] = String(
      Math.max(0, Number(item.paymentPercent) || 0),
    );
    return acc;
  }, {});
};

const sumSubstitutionAmount = (items) =>
  items.reduce((total, item) => total + (Number(item.amount) || 0), 0);

const pickSickLeavePeriod = (detail) => {
  if (!detail || typeof detail !== "object") return null;
  const p = detail.sick_leave_period ?? detail.sickLeavePeriod;
  return typeof p === "string" ? p : p != null ? String(p) : null;
};

const pickCompensationPeriod = (detail) => {
  if (!detail || typeof detail !== "object") return null;
  const p = detail.compensation_period ?? detail.compensationPeriod;
  return typeof p === "string" ? p : p != null ? String(p) : null;
};

const hasCompensationData = (detail) => {
  if (!detail || typeof detail !== "object") return false;
  const amount = coalesceNumeric(detail.compensation_amount);
  if (amount !== null && amount > 0) return true;
  const period = pickCompensationPeriod(detail);
  if (period?.trim()) return true;
  if (coalesceNumeric(detail.compensation_calendar_days) !== null) return true;
  if (
    coalesceNumeric(detail.compensation_workdays, detail.compensationWorkdays) !==
    null
  ) {
    return true;
  }
  return false;
};

const hasVacationData = (detail) => {
  if (!detail || typeof detail !== "object") return false;
  const amount = coalesceNumeric(detail.vacation_amount);
  if (amount !== null && amount > 0) return true;
  const period = detail.vacation_period ?? detail.vacationPeriod;
  if (typeof period === "string" && period.trim()) return true;
  if (period != null && String(period).trim()) return true;
  const calendar = coalesceNumeric(detail.vacation_calendar_days);
  if (calendar !== null && calendar > 0) return true;
  const workdays = coalesceNumeric(
    detail.vacation_workdays,
    detail.vacationWorkdays,
  );
  if (workdays !== null && workdays > 0) return true;
  return false;
};

const pickAverageDailyAmount = (detail, history) =>
  coalesceNumeric(
    detail?.average_salary_per_day,
    detail?.average_day_salary,
    detail?.average_daily_salary,
    detail?.avg_day_salary,
    detail?.avg_daily_salary,
    detail?.average_per_day,
    history?.average_salary_per_day,
    history?.average_day_salary,
    history?.average_daily_salary,
    history?.avg_day_salary,
    history?.avg_daily_salary,
    history?.average_per_day,
  );

/** Лимит выплаты по больничному (25 МРП), тг — из API, МРП в ответе или справочника по году. */
const pickSickLeave25MrpCapTenge = (detail, year) => {
  if (!detail || typeof detail !== "object") return null;
  const explicit = coalesceNumeric(
    detail.sick_leave_25_mrp_limit,
    detail.sick_leave_25_mrp_amount,
    detail.sickLeave25MrpLimit,
    detail.sick_leave_mrp_limit_amount,
    detail.limit_25_mrp,
    detail.limit_25_mrp_amount,
    detail.mrp_limit_25,
    detail.max_sick_leave_by_mrp,
    detail.sick_benefit_cap_25_mrp,
  );
  if (explicit !== null) return explicit;
  const mrpUnit = coalesceNumeric(
    detail.mrp,
    detail.MRP,
    detail.mrp_value,
    detail.monthly_calculation_index,
    detail.monthly_calculation_index_value,
  );
  if (mrpUnit !== null) return mrpUnit * 25;
  const mrpByYear = coalesceNumeric(MRP_BY_YEAR[Number(year)]);
  if (mrpByYear !== null) return mrpByYear * 25;
  return null;
};

const resolveHistoryPeriod = (row) => {
  const directYear = Number(row?.year);
  const directMonth = Number(row?.month);
  if (
    Number.isInteger(directYear) &&
    Number.isInteger(directMonth) &&
    directMonth >= 1 &&
    directMonth <= 12
  ) {
    return { year: directYear, month: directMonth };
  }

  const label = String(row?.month_label ?? "")
    .trim()
    .toLowerCase();
  if (!label) return null;

  const dotMatch = label.match(/(\d{1,2})\.(\d{4})/);
  if (dotMatch) {
    const month = Number(dotMatch[1]);
    const year = Number(dotMatch[2]);
    if (month >= 1 && month <= 12) return { year, month };
  }

  const yearMatch = label.match(/(20\d{2})/);
  const year = yearMatch ? Number(yearMatch[1]) : null;
  const monthName = Object.keys(RU_MONTHS_TO_NUMBER).find((m) =>
    label.includes(m),
  );
  if (year && monthName) {
    return { year, month: RU_MONTHS_TO_NUMBER[monthName] };
  }

  return null;
};

function SalaryCalculationDrawer({
  open,
  onClose,
  onSaved,
  context,
  year,
  month,
  periodLabel,
}) {
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [show12Months, setShow12Months] = useState(false);
  const [editedHistoryAmounts, setEditedHistoryAmounts] = useState({});
  const [substitutionMode, setSubstitutionMode] = useState("percent");
  const [substitutionPercentInputs, setSubstitutionPercentInputs] = useState(
    {},
  );
  const [substitutionFixedInput, setSubstitutionFixedInput] = useState("");
  const [substitutionSavingPaymentId, setSubstitutionSavingPaymentId] =
    useState(null);
  const [substitutionSaveError, setSubstitutionSaveError] = useState(null);
  const [substitutionSaveSuccess, setSubstitutionSaveSuccess] = useState(null);
  const [historySaving, setHistorySaving] = useState(false);
  const [historySaveError, setHistorySaveError] = useState(null);
  const [historySaveSuccess, setHistorySaveSuccess] = useState(null);

  const kind =
    context?.fieldName != null
      ? FIELD_TO_KIND[context.fieldName] || null
      : null;
  const meta = kind ? KIND_META[kind] : null;

  useEffect(() => {
    if (!open || !context?.userId || !kind) {
      setDetail(null);
      setHistory(null);
      setError(null);
      setShow12Months(false);
      setEditedHistoryAmounts({});
      setSubstitutionMode("percent");
      setSubstitutionPercentInputs({});
      setSubstitutionFixedInput("");
      setSubstitutionSavingPaymentId(null);
      setSubstitutionSaveError(null);
      setSubstitutionSaveSuccess(null);
      setHistorySaving(false);
      setHistorySaveError(null);
      setHistorySaveSuccess(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setDetail(null);
      setHistory(null);
      setEditedHistoryAmounts({});
      setSubstitutionSaveError(null);
      setSubstitutionSaveSuccess(null);
      setHistorySaving(false);
      setHistorySaveError(null);
      setHistorySaveSuccess(null);

      try {
        const detailPromise = getSalaryEmployeeDetailV2(context.userId, {
          year,
          month,
        });
        const historyPromise =
          kind === "vacation" || kind === "sick_leave"
            ? getSalaryEmployeeHistoryV2(context.userId, {
                reference_year: year,
                reference_month: month,
              })
            : Promise.resolve(null);

        const [detailRes, historyRes] = await Promise.all([
          detailPromise,
          historyPromise,
        ]);

        if (!cancelled) {
          setDetail(detailRes);
          setHistory(historyRes);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Не удалось загрузить данные");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, context?.userId, context?.fieldName, kind, year, month]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || kind !== "substitution" || !detail) return;
    const substitutionItems = normalizeSubstitutions(detail);
    const initialFixed =
      substitutionItems.length > 0
        ? sumSubstitutionAmount(substitutionItems)
        : coalesceNumeric(detail.substitution_amount, 0);
    setSubstitutionPercentInputs(
      buildSubstitutionPercentInputs(substitutionItems, detail),
    );
    setSubstitutionFixedInput(String(Math.max(0, Number(initialFixed) || 0)));
    setSubstitutionMode("percent");
  }, [open, kind, detail]);

  if (!open || !context) return null;

  const rowTotals = context.rowTotals || {};
  const isPaidOut =
    Number(rowTotals.remaining) === 0 && Number(rowTotals.salary) > 0;

  const vacationAmountResolved = coalesceNumeric(detail?.vacation_amount) ?? 0;
  const compensationAmountResolved =
    coalesceNumeric(detail?.compensation_amount) ?? 0;
  const substitutionItems = normalizeSubstitutions(detail);
  const payoutAmount =
    kind === "vacation"
      ? vacationAmountResolved + compensationAmountResolved
      : kind === "sick_leave"
        ? detail?.sick_leave_amount
        : kind === "substitution"
          ? substitutionItems.length > 0
            ? sumSubstitutionAmount(substitutionItems)
            : detail?.substitution_amount
          : null;

  const substitutionSource = {
    workdays: coalesceNumeric(detail?.workdays, 0) || 0,
    substitutionDays:
      substitutionItems.length > 0
        ? substitutionItems.reduce((total, item) => total + item.days, 0)
        : coalesceNumeric(detail?.substitution_days, 0) || 0,
    percentFromApi:
      substitutionItems.length > 0
        ? substitutionItems[0].paymentPercent
        : coalesceNumeric(detail?.substitution_payment_percent, 0) || 0,
    amountFromApi:
      substitutionItems.length > 0
        ? sumSubstitutionAmount(substitutionItems)
        : coalesceNumeric(detail?.substitution_amount, 0) || 0,
    items: substitutionItems,
  };
  const substitutionPercentValueFor = (item, index) => {
    const key = getSubstitutionItemKey(item, index);
    const raw = substitutionPercentInputs[key];
    if (raw === undefined || raw === "") {
      return Math.max(0, Number(item?.paymentPercent) || 0);
    }
    return Math.max(0, Number(keepDigitsAndDot(raw)) || 0);
  };
  const substitutionFixedValue = Math.max(
    0,
    Number(keepDigitsAndDot(substitutionFixedInput)) || 0,
  );
  const substitutionAbsentSalaries = substitutionItems
    .map((item) => item.absentUserSalary)
    .filter((salary) => salary > 0);
  const substitutionAbsentSalary =
    substitutionAbsentSalaries.length > 0
      ? substitutionAbsentSalaries[0]
      : substitutionSource.workdays > 0 &&
          substitutionSource.substitutionDays > 0 &&
          substitutionSource.percentFromApi > 0
        ? (substitutionSource.amountFromApi * substitutionSource.workdays) /
          (substitutionSource.substitutionDays *
            (substitutionSource.percentFromApi / 100))
        : 0;
  const substitutionHasUniformAbsentSalary =
    substitutionAbsentSalaries.length > 0 &&
    substitutionAbsentSalaries.every(
      (salary) => salary === substitutionAbsentSalaries[0],
    );
  const substitutionAvgDay =
    substitutionSource.workdays > 0 && substitutionHasUniformAbsentSalary
      ? substitutionAbsentSalaries[0] / substitutionSource.workdays
      : 0;
  const substitutionPercentAmount =
    substitutionSource.workdays > 0
      ? substitutionItems.reduce((total, item, index) => {
          const absentSalary =
            item.absentUserSalary > 0
              ? item.absentUserSalary
              : substitutionAbsentSalary;
          const avgDay = absentSalary / substitutionSource.workdays;
          const percent = substitutionPercentValueFor(item, index);
          return total + avgDay * item.days * (percent / 100);
        }, 0)
      : substitutionSource.amountFromApi;
  const substitutionDisplayAmount =
    substitutionMode === "fixed"
      ? substitutionFixedValue
      : substitutionPercentAmount;
  const substitutionSaving = substitutionSavingPaymentId != null;

  const vacationCalendar = detail ? Number(detail.vacation_calendar_days) : 0;
  const vacationWorkingDays = coalesceNumeric(
    detail?.vacation_workdays,
    detail?.vacationWorkdays,
    context.timesheet?.annualLeave,
  );
  const compensationCalendar = coalesceNumeric(
    detail?.compensation_calendar_days,
    detail?.compensationCalendarDays,
  );
  const compensationWorkdays = coalesceNumeric(
    detail?.compensation_workdays,
    detail?.compensationWorkdays,
  );
  const showVacation = hasVacationData(detail);
  const showCompensation = hasCompensationData(detail);
  const sickDaysResolved = coalesceNumeric(
    detail?.sick_leave_days,
    detail?.sickLeaveDays,
    context?.sickLeaveDaysFromPreview,
  );
  const sickLeave25MrpCapExplicit = pickSickLeave25MrpCapTenge(detail, year);
  const historyRows = Array.isArray(history?.history) ? history.history : [];
  const effectiveHistoryRows = historyRows.map((row, index) => {
    const key = `${row.month_label}-${index}`;
    const editedAmount = editedHistoryAmounts[key];
    return {
      ...row,
      amount:
        editedAmount !== undefined ? editedAmount : Number(row.amount) || 0,
      workdays: Number(row.workdays) || 0,
    };
  });
  const totalAmount12Months = coalesceNumeric(history?.total_amount_12_months);
  const workedDays12Months = coalesceNumeric(history?.worked_days_12_months);
  const averagePerDayFromHistory =
    totalAmount12Months !== null &&
    workedDays12Months !== null &&
    workedDays12Months > 0
      ? totalAmount12Months / workedDays12Months
      : null;
  const averagePerDay =
    pickAverageDailyAmount(detail, history) ?? averagePerDayFromHistory;
  const hasHistoryChanges = effectiveHistoryRows.some((row, index) => {
    const original = Number(historyRows[index]?.amount) || 0;
    const current = Number(row.amount) || 0;
    return original !== current;
  });
  const sickLeave25MrpCapResolved = sickLeave25MrpCapExplicit;
  const effectivePayoutAmount = payoutAmount;

  const handleHistoryAmountChange = (key, value) => {
    const cleaned = String(value).replace(/[^\d]/g, "");
    if (cleaned === "") {
      setEditedHistoryAmounts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed)) return;
    setEditedHistoryAmounts((prev) => ({
      ...prev,
      [key]: parsed,
    }));
  };

  const handleSaveHistoryChanges = async () => {
    if (!history || !Array.isArray(history.history) || !hasHistoryChanges) {
      return;
    }

    const rowsToSync = historyRows
      .map((row, index) => {
        const period = resolveHistoryPeriod(row);
        if (!period) {
          return {
            invalid: true,
            label: row?.month_label || "неизвестный месяц",
          };
        }

        const key = `${row.month_label}-${index}`;
        const editedAmount = editedHistoryAmounts[key];
        const amount =
          editedAmount !== undefined ? editedAmount : Number(row.amount) || 0;

        return {
          year: period.year,
          month: period.month,
          amount,
        };
      })
      .sort((a, b) => {
        if (a.invalid || b.invalid) return 0;
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

    const invalidRows = rowsToSync.filter((item) => item.invalid);
    if (invalidRows.length > 0) {
      const invalidLabel = invalidRows[0]?.label || "неизвестный месяц";
      setHistorySaveError(
        `Не удалось определить период для строки "${invalidLabel}".`,
      );
      return;
    }

    setHistorySaving(true);
    setHistorySaveError(null);
    setHistorySaveSuccess(null);

    try {
      let latestResponse = null;
      for (const row of rowsToSync) {
        if (row.invalid) continue;
        latestResponse = await patchSalaryEmployeeHistoryV2(context.userId, {
          year: row.year,
          month: row.month,
          amount: row.amount,
          reason: "Синхронизация истории зарплаты за 12 месяцев",
        });
      }

      if (latestResponse?.history) {
        setHistory(latestResponse.history);
      }
      const [nextDetail, nextHistory] = await Promise.all([
        getSalaryEmployeeDetailV2(context.userId, { year, month }),
        getSalaryEmployeeHistoryV2(context.userId, {
          reference_year: year,
          reference_month: month,
        }),
      ]);
      setDetail(nextDetail);
      setHistory(nextHistory);

      setEditedHistoryAmounts({});
      setHistorySaveSuccess("История зарплаты сохранена");
    } catch (e) {
      setHistorySaveError(
        e?.message || "Не удалось сохранить историю зарплаты",
      );
    } finally {
      setHistorySaving(false);
    }
  };

  const getHistorySaveButtonLabel = () => {
    if (historySaving) return "Сохранение...";
    return "Сохранить";
  };

  const hasHistoryData = historyRows.length > 0;
  const canSaveHistoryChanges =
    hasHistoryChanges && !historySaving && !!context?.userId && hasHistoryData;

  const getHistoryActionVisible = () =>
    show12Months &&
    (hasHistoryChanges || historySaveError || historySaveSuccess);

  const getHistoryInputDisabled = () => historySaving;

  const handleSubstitutionSavePercent = async (item, index) => {
    if (kind !== "substitution") return;

    const paymentId = item?.paymentId;
    if (paymentId === null || paymentId === undefined) {
      setSubstitutionSaveError(
        "Не найден payment_id в substitutions. Обновите данные или пересчитайте месяц.",
      );
      return;
    }

    const percentValue = substitutionPercentValueFor(item, index);

    setSubstitutionSavingPaymentId(paymentId);
    setSubstitutionSaveError(null);
    setSubstitutionSaveSuccess(null);
    try {
      const lastResponse = await patchSubstitutionPaymentPercentV2(paymentId, {
        payment_percent: percentValue,
        reason: "Изменение процента в расчёте замещения",
      });

      const nextDetail = await getSalaryEmployeeDetailV2(context.userId, {
        year,
        month,
      });
      setDetail(nextDetail);
      const nextSubstitutionItems = normalizeSubstitutions(nextDetail);
      const nextAmount = Math.max(
        0,
        nextSubstitutionItems.length > 0
          ? sumSubstitutionAmount(nextSubstitutionItems)
          : coalesceNumeric(
              nextDetail?.substitution_amount,
              lastResponse?.substitution_payment?.amount,
              0,
            ) || 0,
      );
      setSubstitutionPercentInputs(
        buildSubstitutionPercentInputs(nextSubstitutionItems, nextDetail),
      );
      setSubstitutionFixedInput(String(nextAmount));
      setSubstitutionSaveSuccess(
        item.period
          ? `Процент сохранён (${formatPeriodRange(item.period)})`
          : "Процент замещения сохранён",
      );

      if (typeof onSaved === "function") {
        onSaved({
          userId: context.userId,
          fieldName: "substitution",
          amount: nextAmount,
        });
      }
    } catch (e) {
      setSubstitutionSaveError(
        e?.message || "Не удалось сохранить процент замещения",
      );
    } finally {
      setSubstitutionSavingPaymentId(null);
    }
  };

  const renderUnsupported = () => (
    <p className="salary-calc-drawer__muted">
      Для этого показателя детальный расчёт по выбранному полю пока не
      подключён. Используйте кнопку «Расчёты» у отпуска, больничного или
      замещения.
    </p>
  );

  const renderBody = () => {
    if (!kind) return renderUnsupported();
    if (loading) {
      return <p className="salary-calc-drawer__muted">Загрузка данных…</p>;
    }
    if (error) {
      return <p className="salary-calc-drawer__error">{error}</p>;
    }
    if (!detail) return null;

    if (kind === "vacation" || kind === "sick_leave") {
      const periodStr =
        kind === "vacation"
          ? (detail.vacation_period ?? detail.vacationPeriod)
          : pickSickLeavePeriod(detail);
      const daysStrong =
        kind === "vacation" ? (
          <strong>
            {vacationCalendar
              ? `${formatCurrency(vacationCalendar)} к.дн.`
              : "—"}
            {vacationWorkingDays !== null
              ? ` · ${formatCurrency(vacationWorkingDays)} р.дн.`
              : ""}
          </strong>
        ) : (
          <strong>
            {sickDaysResolved !== null
              ? `${formatCurrency(sickDaysResolved)} дн.`
              : "—"}
          </strong>
        );

      return (
        <>
          <button
            type="button"
            className="salary-calc-drawer__toggle"
            onClick={() => setShow12Months((v) => !v)}
          >
            {show12Months ? "Скрыть 12 мес" : "Показать 12 мес"}
            <span className="salary-calc-drawer__chevron">
              {show12Months ? "▲" : "▼"}
            </span>
          </button>

          <div className="salary-calc-drawer__card salary-calc-drawer__card--muted">
            <div className="salary-calc-drawer__row">
              <span>Начислено за 12 мес</span>
              <strong>
                {totalAmount12Months !== null
                  ? formatCurrency(totalAmount12Months)
                  : "—"}
              </strong>
            </div>
            <div className="salary-calc-drawer__row">
              <span>Отработано дней за 12 мес</span>
              <strong>
                {workedDays12Months !== null
                  ? formatCurrency(workedDays12Months)
                  : "—"}
              </strong>
            </div>
            {kind === "vacation" ? (
              <>
                {showVacation ? (
                  <div className="salary-calc-drawer__subsection salary-calc-drawer__subsection--vacation">
                    <div className="salary-calc-drawer__subsection-title">
                      Отпуск
                    </div>
                    <div className="salary-calc-drawer__row">
                      <span>Дни отпуска</span>
                      {daysStrong}
                    </div>
                    <div className="salary-calc-drawer__row">
                      <span>Период</span>
                      <strong>{formatPeriodRange(periodStr)}</strong>
                    </div>
                    {vacationAmountResolved > 0 ? (
                      <div className="salary-calc-drawer__row">
                        <span>Сумма отпускных</span>
                        <strong>
                          {formatCurrency(vacationAmountResolved)} тг
                        </strong>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {showCompensation ? (
                  <div className="salary-calc-drawer__subsection salary-calc-drawer__subsection--compensation">
                    <div className="salary-calc-drawer__subsection-title">
                      Компенсация
                    </div>
                    <div className="salary-calc-drawer__row">
                      <span>Дни компенсации</span>
                      <strong>
                        {[
                          compensationCalendar !== null
                            ? `${formatCurrency(compensationCalendar)} к.дн.`
                            : null,
                          compensationWorkdays !== null
                            ? `${formatCurrency(compensationWorkdays)} р.дн.`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </strong>
                    </div>
                    {compensationAmountResolved > 0 ? (
                      <div className="salary-calc-drawer__row">
                        <span>Сумма</span>
                        <strong>
                          {formatCurrency(compensationAmountResolved)} тг
                        </strong>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="salary-calc-drawer__row">
                  <span>{meta.daysLabel}</span>
                  {daysStrong}
                </div>
                <div className="salary-calc-drawer__row">
                  <span>Период</span>
                  <strong>{formatPeriodRange(periodStr)}</strong>
                </div>
              </>
            )}
            {kind === "sick_leave" && sickLeave25MrpCapResolved !== null ? (
              <div
                className="salary-calc-drawer__mrp-limit"
                role="status"
                aria-label="Лимит выплаты по больничному 25 МРП"
              >
                <span
                  className="salary-calc-drawer__mrp-limit-icon"
                  aria-hidden
                >
                  ⚠
                </span>
                <span className="salary-calc-drawer__mrp-limit-label">
                  Лимит 25 МРП
                </span>
                <strong className="salary-calc-drawer__mrp-limit-value">
                  {formatCurrency(sickLeave25MrpCapResolved)}
                </strong>
              </div>
            ) : null}
          </div>

          {show12Months && historyRows.length > 0 && (
            <div className="salary-calc-drawer__history">
              <div className="salary-calc-drawer__history-head">
                <span>Месяц</span>
                <span>Сумма</span>
                <span>Раб. дни</span>
              </div>
              {effectiveHistoryRows.map((row, i) => {
                const rowKey = `${row.month_label}-${i}`;
                const amountDisplay = formatCurrency(row.amount);
                const inputWidthCh = Math.max(
                  7,
                  Math.min(14, amountDisplay.length + 1),
                );
                return (
                  <div key={rowKey} className="salary-calc-drawer__history-row">
                    <span>{row.month_label}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="salary-calc-drawer__history-input"
                      value={amountDisplay}
                      style={{ "--salary-history-input-ch": inputWidthCh }}
                      onChange={(e) =>
                        handleHistoryAmountChange(rowKey, e.target.value)
                      }
                      disabled={getHistoryInputDisabled()}
                      aria-label={`Сумма за ${row.month_label}`}
                    />
                    <span>{formatCurrency(row.workdays)}</span>
                  </div>
                );
              })}
            </div>
          )}
          {getHistoryActionVisible() && (
            <div className="salary-calc-drawer__history-actions">
              <button
                type="button"
                className="salary-calc-drawer__save-button"
                onClick={handleSaveHistoryChanges}
                disabled={!canSaveHistoryChanges}
              >
                {getHistorySaveButtonLabel()}
              </button>
              {historySaveError ? (
                <p className="salary-calc-drawer__error">{historySaveError}</p>
              ) : null}
              {historySaveSuccess ? (
                <p className="salary-calc-drawer__sub-success">
                  {historySaveSuccess}
                </p>
              ) : null}
            </div>
          )}

          <div className="salary-calc-drawer__section-title">Расчёт</div>
          <div className="salary-calc-drawer__formula">
            {meta.formulaLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>

          <div className="salary-calc-drawer__totals">
            <div className="salary-calc-drawer__row">
              <span>Ср. ЗП в день</span>
              <strong>
                {averagePerDay !== null ? formatCurrency(averagePerDay) : "—"}
              </strong>
            </div>
            {kind === "vacation" &&
            showCompensation &&
            vacationAmountResolved > 0 &&
            compensationAmountResolved > 0 ? (
              <>
                <div className="salary-calc-drawer__row">
                  <span>Отпускные</span>
                  <strong>{formatCurrency(vacationAmountResolved)} тг</strong>
                </div>
                <div className="salary-calc-drawer__row">
                  <span>Компенсация</span>
                  <strong>
                    {formatCurrency(compensationAmountResolved)} тг
                  </strong>
                </div>
              </>
            ) : null}
            <div className="salary-calc-drawer__row salary-calc-drawer__row--highlight">
              <span>Итого к выплате</span>
              <strong>{formatCurrency(effectivePayoutAmount)} тг</strong>
            </div>
          </div>
        </>
      );
    }

    if (kind === "substitution") {
      const canSavePercent = substitutionMode === "percent" && !substitutionSaving;

      return (
        <>
          {substitutionItems.map((item, index) => {
            const absentLabel = formatAbsentUserLabel(item);
            if (!item.period && !absentLabel) return null;
            return (
              <div
                key={`${item.period || "subst"}-${index}`}
                className="salary-calc-drawer__subst-bar"
              >
                <div className="salary-calc-drawer__subst-bar-title">
                  {item.period
                    ? `Замещение: ${formatPeriodRange(item.period)}`
                    : "Замещение"}
                </div>
                {absentLabel ? (
                  <div className="salary-calc-drawer__subst-bar-person">
                    {item.absentUserFullName ? (
                      <span className="salary-calc-drawer__subst-bar-name">
                        {item.absentUserFullName}
                      </span>
                    ) : null}
                    {item.absentUserPosition ? (
                      <span className="salary-calc-drawer__subst-bar-position">
                        {item.absentUserPosition}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}

          <div className="salary-calc-drawer__sub-mode-title">
            Способ расчёта замещения
          </div>
          <div className="salary-calc-drawer__sub-mode-group">
            <label className="salary-calc-drawer__sub-mode-item">
              <input
                type="radio"
                name="substitution-mode"
                checked={substitutionMode === "percent"}
                onChange={() => setSubstitutionMode("percent")}
              />
              <span>По проценту от оклада</span>
            </label>
            <label className="salary-calc-drawer__sub-mode-item">
              <input
                type="radio"
                name="substitution-mode"
                checked={substitutionMode === "fixed"}
                onChange={() => setSubstitutionMode("fixed")}
              />
              <span>Фиксированная сумма</span>
            </label>
          </div>

          <div className="salary-calc-drawer__card">
            {substitutionMode === "percent" ? (
              <>
                {substitutionItems.length > 1 ||
                substitutionItems.some((item) => item.absentUserFullName) ? (
                  substitutionItems.map((item, index) => (
                    <div
                      key={`${item.period || "substitution"}-${index}`}
                      className="salary-calc-drawer__row"
                    >
                      <span>
                        ЗП отсутствующего
                        {item.absentUserFullName
                          ? ` (${item.absentUserFullName})`
                          : item.period
                            ? ` (${formatPeriodRange(item.period)})`
                            : ""}
                      </span>
                      <strong>{formatCurrency(item.absentUserSalary)}</strong>
                    </div>
                  ))
                ) : (
                  <div className="salary-calc-drawer__row">
                    <span>
                      ЗП отсутствующего
                      {substitutionItems[0]?.absentUserFullName
                        ? ` (${substitutionItems[0].absentUserFullName})`
                        : ""}
                    </span>
                    <strong>{formatCurrency(substitutionAbsentSalary)}</strong>
                  </div>
                )}
                <div className="salary-calc-drawer__row">
                  <span>Рабочих дней</span>
                  <strong>{formatCurrency(substitutionSource.workdays)}</strong>
                </div>
                {substitutionItems.length > 1 ? (
                  substitutionItems.map((item, index) => (
                    <div
                      key={`${item.period || "days"}-${index}`}
                      className="salary-calc-drawer__row"
                    >
                      <span>
                        Дни замещения
                        {item.period
                          ? ` (${formatPeriodRange(item.period)})`
                          : ""}
                      </span>
                      <strong>{formatCurrency(item.days)}</strong>
                    </div>
                  ))
                ) : (
                  <div className="salary-calc-drawer__row">
                    <span>Дни замещения</span>
                    <strong>
                      {formatCurrency(substitutionSource.substitutionDays)}
                    </strong>
                  </div>
                )}
                {substitutionItems.map((item, index) => {
                  const itemKey = getSubstitutionItemKey(item, index);
                  const inputId = `substitution-percent-input-${itemKey}`;
                  const isSavingThis =
                    substitutionSavingPaymentId != null &&
                    item.paymentId != null &&
                    substitutionSavingPaymentId === item.paymentId;
                  return (
                    <div
                      key={`percent-${itemKey}`}
                      className="salary-calc-drawer__sub-percent-block"
                    >
                      <div className="salary-calc-drawer__sub-input-row">
                        <label htmlFor={inputId}>
                          % замещения
                          {substitutionItems.length > 1 && item.period
                            ? ` (${formatPeriodRange(item.period)})`
                            : ""}
                        </label>
                        <input
                          id={inputId}
                          className="salary-calc-drawer__sub-input"
                          type="text"
                          inputMode="decimal"
                          value={
                            substitutionPercentInputs[itemKey] ??
                            String(Math.max(0, Number(item.paymentPercent) || 0))
                          }
                          onChange={(e) => {
                            const nextValue = keepDigitsAndDot(e.target.value);
                            setSubstitutionPercentInputs((prev) => ({
                              ...prev,
                              [itemKey]: nextValue,
                            }));
                            setSubstitutionSaveError(null);
                            setSubstitutionSaveSuccess(null);
                          }}
                          disabled={substitutionSaving}
                        />
                      </div>
                      <div className="salary-calc-drawer__sub-actions">
                        <button
                          type="button"
                          className="salary-calc-drawer__save-button"
                          onClick={() =>
                            handleSubstitutionSavePercent(item, index)
                          }
                          disabled={
                            !canSavePercent ||
                            item.paymentId === null ||
                            item.paymentId === undefined
                          }
                        >
                          {isSavingThis ? "Сохранение..." : "Сохранить процент"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="salary-calc-drawer__sub-input-row">
                <label htmlFor="substitution-fixed-input">
                  Фиксированная сумма доплаты, тг
                </label>
                <input
                  id="substitution-fixed-input"
                  className="salary-calc-drawer__sub-input"
                  type="text"
                  inputMode="numeric"
                  value={substitutionFixedInput}
                  onChange={(e) =>
                    setSubstitutionFixedInput(
                      keepDigitsAndDot(e.target.value).replace(/\./g, ""),
                    )
                  }
                />
              </div>
            )}
            {substitutionSaveError ? (
              <p className="salary-calc-drawer__error">
                {substitutionSaveError}
              </p>
            ) : null}
            {substitutionSaveSuccess ? (
              <p className="salary-calc-drawer__sub-success">
                {substitutionSaveSuccess}
              </p>
            ) : null}
          </div>

          {substitutionMode === "percent" ? (
            <>
              <div className="salary-calc-drawer__section-title">Расчёт</div>
              <div className="salary-calc-drawer__formula">
                {meta.formulaLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            </>
          ) : null}

          <div className="salary-calc-drawer__totals">
            {substitutionMode === "percent" ? (
              <div className="salary-calc-drawer__row">
                <span>Средний дневной ЗП</span>
                <strong>{formatCurrency(substitutionAvgDay)}</strong>
              </div>
            ) : null}
            <div className="salary-calc-drawer__row salary-calc-drawer__row--highlight">
              <span>Итого к выплате</span>
              <strong>{formatCurrency(substitutionDisplayAmount)} тг</strong>
            </div>
          </div>
        </>
      );
    }

    return null;
  };

  const title = meta?.title || "Расчёт";
  const badgeClass = isPaidOut
    ? "salary-calc-drawer__badge salary-calc-drawer__badge--paid"
    : "salary-calc-drawer__badge salary-calc-drawer__badge--pending";
  const badgeAmount =
    (kind === "vacation" || kind === "sick_leave"
      ? effectivePayoutAmount
      : kind === "substitution"
        ? substitutionDisplayAmount
        : payoutAmount) ??
    context.amount ??
    rowTotals.paid ??
    0;
  const badgeText = isPaidOut
    ? `Выплачено: ${formatCurrency(badgeAmount)} тг`
    : "Не выплачено";

  return (
    <div className="salary-calc-drawer">
      <button
        type="button"
        className="salary-calc-drawer__backdrop"
        aria-label="Закрыть"
        onClick={onClose}
      />
      <aside
        className="salary-calc-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="salary-calc-drawer-title"
      >
        <div className="salary-calc-drawer__head">
          <h2
            id="salary-calc-drawer-title"
            className="salary-calc-drawer__title"
          >
            {title}
          </h2>
          <div className="salary-calc-drawer__head-right">
            {kind ? <span className={badgeClass}>{badgeText}</span> : null}
            <button
              type="button"
              className="salary-calc-drawer__close"
              onClick={onClose}
              aria-label="Закрыть панель"
            >
              ×
            </button>
          </div>
        </div>

        <div className="salary-calc-drawer__meta">
          <div className="salary-calc-drawer__meta-row">
            <span className="salary-calc-drawer__meta-label">Отделение</span>
            <span>{context.departmentName || "—"}</span>
          </div>
          <div className="salary-calc-drawer__meta-row">
            <span className="salary-calc-drawer__meta-label">Отдел</span>
            <span>{context.groupName || "—"}</span>
          </div>
          <div className="salary-calc-drawer__meta-row">
            <span className="salary-calc-drawer__meta-label">Период</span>
            <span>{periodLabel}</span>
          </div>
          <div className="salary-calc-drawer__meta-row">
            <span className="salary-calc-drawer__meta-label">ФИО</span>
            <span>{context.employeeName || "—"}</span>
          </div>
        </div>

        <div className="salary-calc-drawer__body">{renderBody()}</div>
      </aside>
    </div>
  );
}

export default SalaryCalculationDrawer;
